import { FloatingFocusManager, FloatingPortal } from '@floating-ui/react';
import { memo, type ReactNode } from 'react';

import { usePopoverContext } from '../context';

type Props = {
  children: ReactNode;
  /** Accessible name for the popover, announced when it opens. */
  label: string;
};

// Keyboard Enter/Space synthesize a click with detail 0; a real press is > 0.
function isPointerDriven(event: Event | undefined) {
  return event instanceof MouseEvent && event.detail > 0;
}

export const PopoverContent = memo(({ children, label }: Props) => {
  const { context, floatingStyles, getFloatingProps, isOpen, setFloating } =
    usePopoverContext();

  if (!isOpen) {
    return null;
  }

  // Pointer open: keep focus on the trigger so :focus-visible does not paint.
  const isPointerOpen = isPointerDriven(context.dataRef.current.openEvent);

  return (
    <FloatingPortal>
      <FloatingFocusManager
        context={context}
        initialFocus={isPointerOpen ? -1 : 0}
        modal={false}
        returnFocus={true}
      >
        <div
          aria-label={label}
          className="eds-popover"
          ref={setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
});

PopoverContent.displayName = 'PopoverContent';
